import {
  useCallback,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import {
  createPairForChallenge,
  upsertPlayerProfile,
} from "../../challenges/services/challengeAdminService";
import type { ChallengeDetail } from "../../challenges/types/challengeAdmin";
import type {
  DraftPair,
  DraftPlayer,
} from "../../challenges/types/challengeDraft";
import {
  createPairId,
  normalizeCpf,
} from "../../challenges/utils/challengePairs";
import type { AdminEventRegistration } from "../services/eventRegistrationAdminService";

type UseAdminChallengeParticipantSelectionParams = {
  activeRegistrationCategoryKeyForModal: string;
  challengeDetail: ChallengeDetail | null;
  isTournamentChallenge: boolean;
  nameByCpf: Record<string, string>;
  removedTournamentPairKeysRef: MutableRefObject<Set<string>>;
  setChallengeDetail: Dispatch<SetStateAction<ChallengeDetail | null>>;
  setDraftPairs: Dispatch<SetStateAction<DraftPair[]>>;
  setDraftPlayers: Dispatch<SetStateAction<DraftPlayer[]>>;
};

export function useAdminChallengeParticipantSelection({
  activeRegistrationCategoryKeyForModal,
  challengeDetail,
  isTournamentChallenge,
  nameByCpf,
  removedTournamentPairKeysRef,
  setChallengeDetail,
  setDraftPairs,
  setDraftPlayers,
}: UseAdminChallengeParticipantSelectionParams) {
  const setParticipantsFromPairs = useCallback(
    (nextPairs: DraftPair[]) => {
      const cpfs = Array.from(
        new Set(
          nextPairs.flatMap((pair) => [pair.cpf1, pair.cpf2]).filter(Boolean),
        ),
      );
      setChallengeDetail((prev) =>
        prev ? { ...prev, participantCpfs: cpfs } : prev,
      );
    },
    [setChallengeDetail],
  );

  const setParticipantsFromPlayers = useCallback(
    (nextPlayers: DraftPlayer[]) => {
      const cpfs = Array.from(
        new Set(nextPlayers.map((player) => player.cpf).filter(Boolean)),
      );
      setChallengeDetail((prev) =>
        prev ? { ...prev, participantCpfs: cpfs } : prev,
      );
    },
    [setChallengeDetail],
  );

  const removeRegistrationFromChallengeSelection = useCallback(
    (registration?: AdminEventRegistration | null) => {
      if (!registration) return;
      if (registration.registrationType === "fixed_pair") {
        const cpf1 = normalizeCpf(registration.userCpf);
        const cpf2 = normalizeCpf(registration.partnerCpf);
        if (cpf1.length < 8 || cpf2.length < 8 || cpf1 === cpf2) return;
        const pairId = createPairId(cpf1, cpf2);
        if (isTournamentChallenge) {
          const tournamentId = Number(
            registration.tournamentId || challengeDetail?.id || 0,
          );
          const categoryKey = String(
            registration.registrationCategoryKey ||
              activeRegistrationCategoryKeyForModal ||
              "",
          ).trim();
          removedTournamentPairKeysRef.current.add(
            `${tournamentId}::${categoryKey}::${pairId}`,
          );
        }
        setDraftPairs((prev) => {
          const next = prev.filter((pair) => pair.id !== pairId);
          if (next.length !== prev.length) {
            setParticipantsFromPairs(next);
          }
          return next;
        });
        return;
      }
      const cpf = normalizeCpf(registration.userCpf);
      if (cpf.length < 8) return;
      setDraftPlayers((prev) => {
        const next = prev.filter((player) => normalizeCpf(player.cpf) !== cpf);
        if (next.length !== prev.length) {
          setParticipantsFromPlayers(next);
        }
        return next;
      });
    },
    [
      activeRegistrationCategoryKeyForModal,
      challengeDetail?.id,
      isTournamentChallenge,
      removedTournamentPairKeysRef,
      setDraftPairs,
      setDraftPlayers,
      setParticipantsFromPairs,
      setParticipantsFromPlayers,
    ],
  );

  const persistPairIfNeeded = useCallback(
    async (cpf1: string, cpf2: string) => {
      if (!challengeDetail) return;
      const safeCpf1 = normalizeCpf(cpf1);
      const safeCpf2 = normalizeCpf(cpf2);
      if (!safeCpf1 || !safeCpf2 || safeCpf1 === safeCpf2) return;

      const name1 =
        nameByCpf[safeCpf1] || siteTexts.challengeEngine.labels.player;
      const name2 =
        nameByCpf[safeCpf2] || siteTexts.challengeEngine.labels.player;
      try {
        await Promise.all([
          upsertPlayerProfile(safeCpf1, name1),
          upsertPlayerProfile(safeCpf2, name2),
        ]);
      } catch (_err) {
        // A seleção local não depende da criação do perfil.
      }
      try {
        await createPairForChallenge(
          challengeDetail.challengeType,
          safeCpf1,
          safeCpf2,
        );
      } catch (_err) {
        // A persistência da dupla é oportunista nesta tela.
      }
    },
    [challengeDetail, nameByCpf],
  );

  const ensurePlayerProfile = useCallback(
    async (cpf: string) => {
      const safeCpf = normalizeCpf(cpf);
      if (!safeCpf) return;
      const name =
        nameByCpf[safeCpf] || siteTexts.challengeEngine.labels.player;
      try {
        await upsertPlayerProfile(safeCpf, name);
      } catch (_err) {
        // O cadastro pode ser concluído em outra etapa.
      }
    },
    [nameByCpf],
  );

  return {
    ensurePlayerProfile,
    persistPairIfNeeded,
    removeRegistrationFromChallengeSelection,
    setParticipantsFromPairs,
    setParticipantsFromPlayers,
  };
}
